import { Link } from 'react-router-dom';
import { resolveContext } from '@/state/session';
import {
  getAccounts,
  getCards,
  getFinanceSummary,
  getNotifications,
  getPendingApprovals,
  getTransactions,
} from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { Notice } from '@/components/feedback';
import {
  defaultWidgetOrder,
  enforcePinning,
  widgetById,
  type MobileWidgetId,
} from '@/customisation/widgetRegistry';
import { WIDGET_COMPONENTS, type HomeData } from './widgets';

/**
 * Mobile home.
 *
 * TRACE:TT-R109-01 — a curated, reduced widget set, not the full web dashboard.
 * TRACE:TT-R109-02 — mobile carries fewer widget functionalities than SME Web
 * while keeping the same concepts and the same vocabulary.
 * TRACE:TT-R130-02 — whether a white-label tenant should get a cards-first
 * dashboard is an open configuration decision, recorded in
 * build_state/DECISIONS.md rather than silently chosen here.
 *
 * The page renders a governed widget order. Personalisation and the AI composer
 * change that order through the same registry and the same pinning rules, so
 * neither route can violate an ordering requirement.
 */
export function HomePage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  const transactions = useAsync(async () => {
    if (!companyId || !userId) return [];
    return [...(await getTransactions(companyId, userId, {}, null, 10)).data.items];
  }, [companyId, userId]);

  const approvals = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getPendingApprovals(companyId, userId)).data;
  }, [companyId, userId]);

  const cards = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getCards(companyId, userId)).data;
  }, [companyId, userId]);

  const summary = useAsync(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await getFinanceSummary(companyId, userId, 30)).data;
  }, [companyId, userId]);

  const notifications = useAsync(async () => {
    if (!companyId) return [];
    return (await getNotifications(companyId)).data;
  }, [companyId]);

  if (!resolved) return null;

  const ctx = resolved.entitlements;
  const data: HomeData = {
    accounts,
    transactions,
    approvals,
    cards,
    summary,
    baseCurrency: resolved.company.baseCurrency,
    canCreatePayment: resolveCapability(ctx, CAPABILITIES.paymentsCreate).allowed,
    canExpense: resolveCapability(ctx, CAPABILITIES.expenseEntry).allowed,
    canFx: resolveCapability(ctx, CAPABILITIES.fxView).allowed,
  };

  // Only widgets the user is entitled to, in the governed order.
  const order: readonly MobileWidgetId[] = enforcePinning(
    defaultWidgetOrder().filter((id) => {
      const definition = widgetById(id);
      if (!definition) return false;
      if (!definition.capability) return true;
      return resolveCapability(ctx, CAPABILITIES[definition.capability]).allowed;
    }),
  );

  const unread = notifications.data?.filter((n) => !n.read).length ?? 0;
  const firstName = resolved.user.name.split(' ')[0] ?? resolved.user.name;

  return (
    <div className="tm-stack">
      <header
        className="tm-header tm-header--home"
        style={{ marginInline: 'calc(var(--tm-gutter) * -1)' }}
      >
        <div>
          <p className="tm-eyebrow">{resolved.company.tradingName}</p>
          <h1 className="tm-header__title">Hello, {firstName}</h1>
        </div>
        <Link
          to="/mobile/notifications"
          className="tm-icon-btn"
          style={{ position: 'relative' }}
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications, none unread'
          }
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 9a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
            <path d="M10.5 20a2 2 0 0 0 3 0" />
          </svg>
          {unread > 0 ? (
            <span className="tm-count" style={{ position: 'absolute', top: 4, insetInlineEnd: 4 }}>
              {unread}
            </span>
          ) : null}
        </Link>
      </header>

      {/* A restricted or onboarding company explains its impact up front. */}
      {resolved.company.status !== 'active' && resolved.company.statusReason ? (
        <Notice tone="warning" title={`Your company is ${resolved.company.status}`}>
          {resolved.company.statusReason}
        </Notice>
      ) : null}

      {order.map((id) => {
        const Widget = WIDGET_COMPONENTS[id];
        return <Widget key={id} data={data} />;
      })}

      <div style={{ paddingBlockStart: 4 }}>
        <Link to="/mobile/settings/personalisation" className="tm-btn tm-btn--secondary tm-btn--block">
          Edit home
        </Link>
      </div>
    </div>
  );
}
